import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FEATURE_FLAGS } from '../../../../core/common/constants/feature-flags.constants';
import { FeatureFlagsService } from '../../../../core/common/services/feature-flags/feature-flags.service';
import { MenuItem } from '../../sidenav/sidenav.model';
import { TooltipDirective } from '../../tooltip/tooltip.directive';
import {
  SIDE_MENU_ITEM_ICONS,
  SideMenuIconComponent,
  SideMenuIconName,
} from '../side-menu-icon/side-menu-icon.component';
import { SideMenuQuickActionComponent } from '../side-menu-quick-action/side-menu-quick-action.component';

/** Outline artwork stored as a filled ring. A CSS stroke traces both edges and looks thick. */
const FILLED_OUTLINE_ICONS = new Set<SideMenuIconName>([
  'users-group',
  'tag',
  'shield-user',
  'document',
  'shield-tick',
  'table-settings',
  'vendor-permission',
  'vendor-data',
]);

/** Lucide outlines: they carry their own stroke on circles and rects too, so the menu only sets the colour. */
const LUCIDE_ICONS = new Set<SideMenuIconName>([
  'home',
  'tasks',
  'calendar',
  'workflow',
  'package',
  'vendors',
  'invoice',
  'borrowing',
  'clock',
  'team',
  'building',
  'network',
]);

// The tooltip directive is only attached while collapsed: an empty libTooltip
// would still open an empty bubble on hover, and the directive binds its
// listeners once, so the branch has to be structural.
@Component({
  selector: 'lib-side-menu-item',
  imports: [
    RouterLink,
    RouterLinkActive,
    NgTemplateOutlet,
    TooltipDirective,
    SideMenuIconComponent,
    SideMenuQuickActionComponent,
  ],
  templateUrl: './side-menu-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideMenuItemComponent {
  private readonly featureFlags = inject(FeatureFlagsService);

  readonly item = input.required<MenuItem>();
  readonly collapsed = input<boolean>(false);

  protected readonly systemDefaultColor = computed(() =>
    this.featureFlags.getFeatureFlagValue(
      FEATURE_FLAGS.SYSTEM.system_default_color
    )
  );
  /** Dark pill when the system palette is neutral; the primary tint otherwise. */
  protected readonly activeLinkClass = computed(() =>
    this.systemDefaultColor() ? '' : 'bg-primary-50 text-primary-700'
  );
  /**
   * These icons are already drawn as a filled outline. Stroking that ring
   * traces both edges, so the mark stays the original fill and only the colour changes.
   */
  private readonly filledOutlineIcon = computed(() => {
    const icon = this.figmaIcon();
    return icon ? FILLED_OUTLINE_ICONS.has(icon) : false;
  });
  private readonly lucideIcon = computed(() => {
    const icon = this.figmaIcon();
    return icon ? LUCIDE_ICONS.has(icon) : false;
  });
  /** Stroke icons: transparent fill, thin stroke. Filled outlines and Lucide icons keep their own weight. */
  protected readonly inactiveIconClass = computed(() =>
    this.iconClassFor('text-primary-500')
  );
  protected readonly activeIconClass = computed(() =>
    this.systemDefaultColor()
      ? this.iconClassFor('text-white')
      : this.inactiveIconClass()
  );
  protected readonly inactiveTemplateIconClass =
    'inline-flex shrink-0 [&_svg]:size-[1rem] [&_path]:fill-primary-500 [&_path]:stroke-0 [&_path]:opacity-100';
  protected readonly activeTemplateIconClass = computed(() =>
    this.systemDefaultColor()
      ? 'inline-flex shrink-0 [&_svg]:size-[1rem] [&_path]:fill-white [&_path]:stroke-0 [&_path]:opacity-100'
      : this.inactiveTemplateIconClass
  );

  // The rail sits at the physical right edge in RTL; language changes reload the page, so reading dir once is enough.
  protected readonly tooltipPlacement = computed<'left' | 'right'>(() =>
    document.dir === 'rtl' ? 'left' : 'right'
  );
  protected readonly linkClass = computed(() => {
    const layout = this.collapsed()
      ? 'justify-center px-0'
      : 'min-w-0 flex-1 px-md py-[1.112rem]';
    const base =
      'flex h-3xl items-center gap-sm rounded-md text-sm font-normal text-neutral-700 hover:bg-neutral-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 ' +
      layout;
    if (!this.systemDefaultColor()) {
      return base;
    }
    return (
      base +
      ' aria-[current=page]:bg-neutral-900 aria-[current=page]:text-white aria-[current=page]:hover:bg-neutral-800'
    );
  });
  protected readonly key = computed(() => this.item().id ?? this.item().label);
  /** This page's side-menu icon (Figma's, or the admin pick); otherwise the item's own iconTemplate. */
  protected readonly figmaIcon = computed(() => {
    const id = this.item().id;
    return id ? SIDE_MENU_ITEM_ICONS[id] : undefined;
  });
  private iconClassFor(colour: string): string {
    if (this.lucideIcon()) {
      return `size-[1rem] ${colour}`;
    }
    return this.filledOutlineIcon()
      ? `size-[1rem] ${colour} [&_path]:fill-current [&_path]:stroke-0 [&_path]:opacity-100`
      : `size-[1rem] ${colour} [&_path]:fill-transparent [&_path]:stroke-current [&_path]:[stroke-width:1] [&_path]:opacity-100`;
  }
  protected readonly labelClass = computed(() =>
    this.collapsed() ? 'sr-only' : 'truncate'
  );
}
